import { VALIDATION_MESSAGES } from '@/constants/common/validation.constants'
import { type ShipmentOrdersListExEntity } from '@/libs/api'
import { safeFormatISO } from '@/libs/utils'

/** 日付を揃えず各依頼の日付を維持する選択（ラジオの初期値） */
export const KEEP_BUNDLE_DATE = ''

/**
 * 日付項目ごとの選択モード。
 * align はカレンダーで指定した1日に全依頼を揃える、keep は各依頼の日付を維持する。
 */
export type BundleDateAlignMode = 'align' | 'keep'

/**
 * 請求計上日の選択モード（BE の incomeRecordingDateHandling に対応）。
 * follow は揃えた発日・着日から計上基準で再算出（既定）、
 * align はカレンダーで指定した1日に全依頼を揃える、keep は各依頼の計上日を維持する。
 */
export type IncomeRecordingDateMode = 'follow' | 'align' | 'keep'

/** IncomeRecordingDateMode → BE の incomeRecordingDateHandling への変換 */
export const toIncomeRecordingDateHandling = (
  mode: IncomeRecordingDateMode
): 'FOLLOW' | 'ALIGN' | 'KEEP' => {
  if (mode === 'align') return 'ALIGN'
  if (mode === 'keep') return 'KEEP'
  return 'FOLLOW'
}

export type BundleDateOption = {
  /** yyyy-MM-dd。表示とユニーク判定に使う */
  key: string
  /**
   * API へ送る値。選択行が持つ元の日時文字列をそのまま使う。
   * yyyy-MM-dd へ丸めるとタイムゾーン差で既存グループと別時刻になり得るため。
   */
  value: string
}

/** ラジオの選択値。空文字は「そのまま」 */
export type BundleDateSelection = string

export type BundleDateAlignmentOptions = {
  shippingDateOptions: BundleDateOption[]
  deliveryDateOptions: BundleDateOption[]
  incomeRecordingDateOptions: BundleDateOption[]
  /**
   * 発日・着日のいずれかが複数日付 = 揃える選択をユーザーに求める。
   * 計上日は発日・着日への追従が既定のため、割れていても統一は求めない
   */
  needsAlignment: boolean
}

const toDateOptions = (values: string[]): BundleDateOption[] => {
  const optionByKey = new Map<string, BundleDateOption>()
  values.forEach((value) => {
    const key = safeFormatISO(value)
    if (!key || optionByKey.has(key)) {
      return
    }
    optionByKey.set(key, { key, value })
  })
  return [...optionByKey.values()].sort((a, b) => a.key.localeCompare(b.key))
}

/**
 * 束ね対象の発日・着日のユニーク日付を集める。
 * 片側だけ複数のケースでも揃える選択を出せるよう、両側の選択肢を常に返す。
 */
export const collectBundleDateOptions = (
  orders: ShipmentOrdersListExEntity[]
): BundleDateAlignmentOptions => {
  const shippingDateOptions = toDateOptions(
    orders.map((order) => order.group.shippingDate)
  )
  const deliveryDateOptions = toDateOptions(
    orders.map((order) => order.group.deliveryDate)
  )
  const incomeRecordingDateOptions = toDateOptions(
    orders.map((order) => order.incomeRecordingDate)
  )

  return {
    shippingDateOptions,
    deliveryDateOptions,
    incomeRecordingDateOptions,
    needsAlignment:
      shippingDateOptions.length > 1 || deliveryDateOptions.length > 1
  }
}

/**
 * カレンダーで選んだ日付を API へ送る値に変換する。
 *
 * 選択肢と同じ日を選んだときは選択肢の元の日時文字列を使う（yyyy-MM-dd へ丸めると
 * タイムゾーン差で既存グループと別時刻になり得るため）。選択肢にない日はカレンダーの
 * 日付から ISO 文字列を作る（配送依頼の作成・複製と同じ送り方）。
 *
 * @returns API へ送る日付文字列。日付が未入力なら KEEP_BUNDLE_DATE
 */
export const resolveBundleDateValue = (
  options: BundleDateOption[],
  date: Date | null | undefined
): BundleDateSelection => {
  if (!date) {
    return KEEP_BUNDLE_DATE
  }
  const key = safeFormatISO(date)
  if (!key) {
    return KEEP_BUNDLE_DATE
  }
  const matched = options.find((option) => option.key === key)
  return matched ? matched.value : date.toISOString()
}

/**
 * 選択した日付で「着日 < 発日」になる依頼がないか検証する。
 * 「そのまま」を選んだ側は依頼ごとの現行日付と突き合わせる。
 *
 * @returns エラーメッセージ。問題なければ null
 */
export const validateBundleDateSelection = ({
  orders,
  shippingDate,
  deliveryDate
}: {
  orders: ShipmentOrdersListExEntity[]
  shippingDate: BundleDateSelection
  deliveryDate: BundleDateSelection
}): string | null => {
  const selectedShippingKey = shippingDate ? safeFormatISO(shippingDate) : ''
  const selectedDeliveryKey = deliveryDate ? safeFormatISO(deliveryDate) : ''

  const hasInvalidOrder = orders.some((order) => {
    const shippingKey =
      selectedShippingKey || safeFormatISO(order.group.shippingDate)
    const deliveryKey =
      selectedDeliveryKey || safeFormatISO(order.group.deliveryDate)
    if (!shippingKey || !deliveryKey) {
      return false
    }
    return deliveryKey < shippingKey
  })

  return hasInvalidOrder
    ? VALIDATION_MESSAGES.DELIVERY_DATE_BEFORE_SHIPPING_DATE
    : null
}
